import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import BaseLayout from 'bento-ordering/base/layouts/Layout';
import { useTheme } from '@mui/material/styles';
import DarkCover from 'components/DarkCover';

import { getAllLocationsAvailableFulfillmentMethodSelector } from 'bento-ordering/locations/selectors';
import LocationListHeroContent from 'components/Location/LocationListHeroContent';
import { ALL_FULFILLMENT_METHODS } from 'constants/fulfillmentOptions';
import { handleAddressSelectLocationList } from 'bento-ordering/locations/actions/locations';
import { setCartAddressAndGetLocationsInRelation } from 'bento-ordering/locations/actions/dinerAddressDeliverable';
import { getAddressStringFromCart } from 'helpers/addressHelpers';
import isDineInApplicationVariant from 'bento-ordering/utils/isDineInApplicationVariant';
import DineInHeader from 'bento-ordering/locations/components/DineInHeader';
import FulfillmentMethodAvailabilityHeader from 'components/Location/FulfillmentMethodAvailabilityHeader';
import { getHasHeroImage } from 'helpers/getHasHeroImage';
import { GeocodeProvider } from 'bento-ordering/context/GeocodeContext';

const LocationListOuterLayout = ({
  children,
  heroImage,
  availableFulfillmentMethods,
  dispatch,
  googleLoaded,
  cart,
  isLocationListSortingEnabled,
  locationsFetchedForGooglePlaceId
}) => {
  const isDineInVariant = isDineInApplicationVariant();
  const handleAddressSelect = (addressComponents, selectedLatLng, placeId) => {
    dispatch(
      handleAddressSelectLocationList(
        addressComponents,
        placeId,
        selectedLatLng
      )
    );
  };
  const hasHeroImage = getHasHeroImage(heroImage);
  const getHeroHeader = (availableFulfillmentMethods, hasHeroImage) => {
    if (isDineInVariant) {
      return <DineInHeader />;
    }

    return (
      <FulfillmentMethodAvailabilityHeader
        availableFulfillmentMethods={availableFulfillmentMethods}
        hasHeroImage={hasHeroImage}
      />
    );
  };
  const heroHeader = getHeroHeader(availableFulfillmentMethods, hasHeroImage);

  const theme = useTheme();

  React.useEffect(() => {
    if (
      cart.shipping_google_place_id &&
      cart.shipping_google_place_id !== locationsFetchedForGooglePlaceId
    ) {
      const payload = {
        shipping_street_address_1: cart.shipping_street_address_1,
        shipping_city: cart.shipping_city,
        shipping_postal_code: cart.shipping_postal_code,
        shipping_google_place_id: cart.shipping_google_place_id,
        shipping_state: cart.shipping_state,
        shipping_lat: cart.shipping_lat,
        shipping_lng: cart.shipping_lng
      };
      dispatch(setCartAddressAndGetLocationsInRelation(payload));
    }
  }, [
    cart.shipping_google_place_id,
    dispatch,
    locationsFetchedForGooglePlaceId
  ]);

  const backgroundColor = theme.palette.button.background.hover;

  return (
    /**
     * render the hero image only when locations are available as well
     */
    <GeocodeProvider>
      <BaseLayout
        heroImage={heroImage}
        heroContent={
          <DarkCover
            dataTestId="dark-cover"
            background={hasHeroImage ? '' : backgroundColor}
          >
            {googleLoaded && (
              <LocationListHeroContent
                heading={heroHeader}
                handleSelect={handleAddressSelect}
                address={getAddressStringFromCart(cart)}
                showAddressAutoComplete={!isDineInVariant}
                isLocationListSortingEnabled={isLocationListSortingEnabled}
                hasHeroImage={hasHeroImage}
              />
            )}
          </DarkCover>
        }
      >
        {children}
      </BaseLayout>
    </GeocodeProvider>
  );
};

LocationListOuterLayout.propTypes = {
  children: PropTypes.object,
  heroImage: PropTypes.object,
  availableFulfillmentMethods: PropTypes.arrayOf(
    PropTypes.oneOf(ALL_FULFILLMENT_METHODS)
  ),
  dispatch: PropTypes.func.isRequired,
  googleLoaded: PropTypes.bool,
  cart: PropTypes.shape({
    shipping_street_address_1: PropTypes.string,
    shipping_city: PropTypes.string,
    shipping_state: PropTypes.string,
    shipping_postal_code: PropTypes.string,
    shipping_google_place_id: PropTypes.string,
    shipping_lat: PropTypes.number,
    shipping_lng: PropTypes.number
  }),
  isLocationListSortingEnabled: PropTypes.bool,
  locationsFetchedForGooglePlaceId: PropTypes.string
};

const mapStateToProps = state => {
  const image = get(
    state.account,
    'online_ordering_general_settings.store_header_image[0]',
    {}
  );
  const availableFulfillmentMethods = getAllLocationsAvailableFulfillmentMethodSelector(
    state.restaurantLocations,
    state.locationThrottledTimes,
    state.cart
  );

  const googleLoaded = state.libraryLoad.google;
  const isLocationListSortingEnabled = get(
    state.account,
    'location_list_sorting_enabled',
    true
  );
  return {
    heroImage: image,
    availableFulfillmentMethods,
    googleLoaded,
    cart: state.cart,
    isLocationListSortingEnabled,
    locationsFetchedForGooglePlaceId: state.restaurantLocations.googlePlaceId
  };
};

export default connect(mapStateToProps)(React.memo(LocationListOuterLayout));
